/**
 * ICS (iCalendar, RFC 5545) generation for tech-event schedule items.
 *
 * Pure and free of Volto/browser imports so it can be unit-tested and
 * later moved upstream to @plone-collective/volto-techevent as-is.
 * The DOM download glue lives in IcsDownloadButton.jsx.
 *
 * Timestamps are emitted in UTC (`...Z`): always valid, timezone-safe in
 * every calendar client, and no VTIMEZONE block needed.
 */

const CRLF = '\r\n';
const DEFAULT_PRODID = '-//ploneconf.org//techevent-schedule//EN';

/** Escape a text value per RFC 5545 §3.3.11. */
export function escapeIcsText(value) {
  return String(value)
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

/** Fold a content line to 75 octets with CRLF + single-space continuations. */
export function foldIcsLine(line) {
  const encoder = new TextEncoder();
  const parts = [];
  let current = '';
  let currentBytes = 0;
  for (const char of line) {
    const charBytes = encoder.encode(char).length;
    if (currentBytes + charBytes > 75) {
      parts.push(current);
      current = char;
      currentBytes = charBytes;
    } else {
      current += char;
      currentBytes += charBytes;
    }
  }
  parts.push(current);
  return parts.join(CRLF + ' ');
}

/** Format an ISO 8601 timestamp as an iCalendar UTC date-time. */
export function formatIcsUtc(iso) {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}` +
    `T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
  );
}

function line(name, value) {
  return foldIcsLine(`${name}:${value}`);
}

/**
 * Build one VEVENT.
 * props: { uid, title, start, end, location?, description?, url?, categories? }
 * options.stamp: Date used for DTSTAMP (injectable for tests).
 */
export function buildVEvent(props, options = {}) {
  const stamp = options.stamp ?? new Date();
  const lines = [
    'BEGIN:VEVENT',
    line('UID', props.uid),
    line('DTSTAMP', formatIcsUtc(stamp.toISOString())),
    line('DTSTART', formatIcsUtc(props.start)),
    line('DTEND', formatIcsUtc(props.end)),
    line('SUMMARY', escapeIcsText(props.title)),
  ];
  if (props.location)
    lines.push(line('LOCATION', escapeIcsText(props.location)));
  if (props.description) {
    lines.push(line('DESCRIPTION', escapeIcsText(props.description)));
  }
  if (props.url) lines.push(line('URL', props.url));
  if (props.categories?.length) {
    lines.push(
      line('CATEGORIES', props.categories.map(escapeIcsText).join(',')),
    );
  }
  lines.push('END:VEVENT');
  return lines.join(CRLF);
}

/** Build a full VCALENDAR document for one or more event prop objects. */
export function buildIcs(events, options = {}) {
  const prodid = options.prodid ?? DEFAULT_PRODID;
  const body = events.map((event) => buildVEvent(event, options)).join(CRLF);
  return (
    [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      `PRODID:${prodid}`,
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
    ].join(CRLF) +
    CRLF +
    body +
    CRLF +
    'END:VCALENDAR' +
    CRLF
  );
}

function asArray(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function titles(value) {
  return asArray(value)
    .map((item) => item?.title ?? item)
    .filter(Boolean);
}

function composeBody({ description, speakers, tags }) {
  const parts = [];
  const text = description?.trim();
  if (text) parts.push(text);
  if (speakers.length) parts.push(`Speaker: ${speakers.join(', ')}`);
  if (tags.length) parts.push(tags.map((tag) => `#${tag}`).join(' '));
  return parts.length ? parts.join('\n') : undefined;
}

function chipTitles(item) {
  return [
    ...titles(item.session_level),
    ...titles(item.session_audience),
    ...titles(item.session_language),
    ...titles(item.duration ?? item.session_duration),
  ];
}

function mapCommon(item, options) {
  const host = options.host ?? 'localhost';
  const tracks = titles(item.session_track);
  const rooms = titles(item.room);
  const speakers = titles(item.presenters);
  // slot summaries carry the catalog `Subject`, full content the `subjects` field
  const subjects = asArray(item.Subject ?? item.subjects).filter(Boolean);
  const tags = [...new Set([...subjects, ...chipTitles(item)])];
  return {
    uid: `${item.UID ?? item['@id']}@${host}`,
    title: item.title,
    start: item.start,
    end: item.end,
    location: tracks[0] ?? rooms[0] ?? undefined,
    description: composeBody({
      description: item.description,
      speakers,
      tags,
    }),
    url: options.url ?? item['@id'],
    categories: tags.length ? tags : undefined,
  };
}

/** Map a schedule slot (summary serialization) to event props. */
export function slotToIcsProps(slot, options = {}) {
  return mapCommon(slot, options);
}

/** Map a full session content object to event props. */
export function contentToIcsProps(content, options = {}) {
  return mapCommon(content, options);
}

/** Filesystem-safe slug for the download filename. */
export function slugify(title) {
  if (title == null) return '';
  return String(title)
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[áàâ]/g, 'a')
    .replace(/[éèêë]/g, 'e')
    .replace(/[íìîï]/g, 'i')
    .replace(/[óòô]/g, 'o')
    .replace(/[úùû]/g, 'u')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
}

/**
 * Make the session URL absolute: calendar clients cannot resolve
 * relative URLs, so prefix with the origin of the site generating the
 * file. Already-absolute URLs pass through unchanged.
 */
export function toAbsoluteUrl(url, origin) {
  if (/^https?:\/\//.test(url)) return url;
  return new URL(url, origin).href;
}
